import { IColor4 } from '../../../api.js';
export class MeshDescriptions {
    constructor() { }
    static create_arrow(shaft_length, shaft_radius, cone_height, cone_radius, shaft_color, cone_color) {
        const shaft_detail = 12; // Number of sides of the cylinder
        const cone_detail = 12; // Number of sides of the cone
        // Assemble the arrow by translating the cone above the cylinder
        const shaft = MeshDescriptions.create_cylinder(shaft_length, shaft_radius, shaft_color, shaft_detail);
        const cone = MeshDescriptions.create_cone(cone_height, cone_radius, cone_color, cone_detail);
        // Translate cone positions
        for (let i = 0; i < cone.positions.length; i += 3) {
            cone.positions[i + 1] += shaft_length / 2; // Shift along the y-axis
        }
        // Combine the geometries
        const positions = new Float32Array([...shaft.positions, ...cone.positions]);
        const colors = new Float32Array([...shaft.colors, ...cone.colors]);
        const indices = new Uint16Array([...shaft.indices, ...cone.indices.map(index => index + shaft.positions.length / 3)]);
        return {
            positions: positions,
            indices: indices,
            colors: colors
        };
    }
    static create_scale_arrow(shaft_length, shaft_radius, cube_side_size, shaft_color, cube_color) {
        const shaft_detail = 12; // Number of sides of the cylinder
        // Assemble the arrow by translating the cone above the cylinder
        const shaft = MeshDescriptions.create_cylinder(shaft_length, shaft_radius, shaft_color, shaft_detail);
        const cone = MeshDescriptions.create_cube(cube_side_size, cube_color);
        // Translate cone positions
        for (let i = 0; i < cone.positions.length; i += 3) {
            cone.positions[i + 1] += shaft_length / 2; // Shift along the y-axis
        }
        // Combine the geometries
        const positions = new Float32Array([...shaft.positions, ...cone.positions]);
        const colors = new Float32Array([...shaft.colors, ...cone.colors]);
        const indices = new Uint16Array([...shaft.indices, ...cone.indices.map(index => index + shaft.positions.length / 3)]);
        return {
            positions: positions,
            indices: indices,
            colors: colors
        };
    }
    static create_cylinder(height, radius, color, detail) {
        const positions = [];
        const colors = [];
        const indices = [];
        const angle_step = (Math.PI * 2) / detail;
        const color_array = IColor4.to_array(color);
        // Create top and bottom circle vertices
        for (let i = 0; i <= detail; i++) {
            const angle = i * angle_step;
            const x = Math.cos(angle) * radius;
            const z = Math.sin(angle) * radius;
            // Top circle vertex
            positions.push(x, height / 2, z);
            colors.push(...color_array);
            // Bottom circle vertex
            positions.push(x, -height / 2, z);
            colors.push(...color_array);
        }
        // Create indices for the top circle, bottom circle, and sides
        for (let i = 0; i < detail; i++) {
            const top1 = 2 * i;
            const bottom1 = 2 * i + 1;
            const top2 = 2 * i + 2;
            const bottom2 = 2 * i + 3;
            // Top circle triangle
            indices.push(detail * 2, top2, top1);
            // Bottom circle triangle
            indices.push(bottom1, bottom2, detail * 2 + 1);
            // Side faces
            indices.push(top1, top2, bottom1);
            indices.push(bottom1, top2, bottom2);
        }
        // Center vertices for the top and bottom circles
        positions.push(0, height / 2, 0); // top center vertex
        colors.push(...color_array);
        positions.push(0, -height / 2, 0); // bottom center vertex
        colors.push(...color_array);
        return {
            positions: new Float32Array(positions),
            indices: new Uint16Array(indices),
            colors: new Float32Array(colors)
        };
    }
    static create_cone(height, radius, color, detail) {
        const positions = [];
        const colors = [];
        const indices = [];
        const angle_step = (Math.PI * 2) / detail;
        const color_array = IColor4.to_array(color);
        // Base circle vertices and center vertex
        for (let i = 0; i <= detail; i++) {
            const angle = i * angle_step;
            const x = Math.cos(angle) * radius;
            const z = Math.sin(angle) * radius;
            positions.push(x, 0, z); // Base circle vertex
            colors.push(...color_array);
        }
        // Tip of the cone
        positions.push(0, height, 0); // Tip vertex at the center top
        colors.push(...color_array);
        // Base center vertex for easy base triangulation
        positions.push(0, 0, 0); // Center of the base circle
        colors.push(...color_array);
        // Create indices for the cone's surface and base
        const tip_index = detail + 1;
        const base_center_index = detail + 2;
        for (let i = 0; i < detail; i++) {
            const current = i;
            const next = (i + 1) % detail;
            // Side face triangle (tip, next, current)
            indices.push(tip_index, next, current);
            // Base triangle (current, next, baseCenterIndex)
            indices.push(current, next, base_center_index);
        }
        return {
            positions: new Float32Array(positions),
            indices: new Uint16Array(indices),
            colors: new Float32Array(colors)
        };
    }
    static create_plane(width, height, color) {
        const positions = [];
        const colors = [];
        const indices = [];
        // Vertex positions
        positions.push(-width / 2, 0, -height / 2); // Bottom left
        positions.push(width / 2, 0, -height / 2); // Bottom right
        positions.push(width / 2, 0, height / 2); // Top right
        positions.push(-width / 2, 0, height / 2); // Top left
        // Vertex colors
        for (let i = 0; i < 4; i++) {
            colors.push(...IColor4.to_array(color));
        }
        // Indices for two triangles for one side
        indices.push(0, 1, 2);
        indices.push(2, 3, 0);
        // Indices for two triangles for the opposite side
        indices.push(2, 1, 0);
        indices.push(0, 3, 2);
        return {
            positions: new Float32Array(positions.flat()),
            indices: new Uint16Array(indices),
            colors: new Float32Array(colors)
        };
    }
    static create_torus(major_radius, minor_radius, major_segments, minor_segments, color) {
        const positions = [];
        const colors = [];
        const indices = [];
        for (let i = 0; i <= major_segments; i++) {
            const theta = (i / major_segments) * 2 * Math.PI;
            const sin_theta = Math.sin(theta);
            const cos_theta = Math.cos(theta);
            for (let j = 0; j <= minor_segments; j++) {
                const phi = (j / minor_segments) * 2 * Math.PI;
                const sin_phi = Math.sin(phi);
                const cos_phi = Math.cos(phi);
                const x = (major_radius + minor_radius * cos_phi) * cos_theta;
                const y = minor_radius * sin_phi;
                const z = (major_radius + minor_radius * cos_phi) * sin_theta;
                positions.push(x, y, z);
                colors.push(...IColor4.to_array(color));
            }
        }
        for (let i = 0; i < major_segments; i++) {
            for (let j = 0; j < minor_segments; j++) {
                const first = (i * (minor_segments + 1)) + j;
                const second = first + minor_segments + 1;
                indices.push(first, second, first + 1);
                indices.push(second, second + 1, first + 1);
            }
        }
        return {
            positions: new Float32Array(positions),
            colors: new Float32Array(colors),
            indices: new Uint16Array(indices)
        };
    }
    static create_cube(side_length, color) {
        const half_side = side_length / 2;
        const positions = [
            // Front face
            -half_side, -half_side, half_side, // Bottom left
            half_side, -half_side, half_side, // Bottom right
            half_side, half_side, half_side, // Top right
            -half_side, half_side, half_side, // Top left
            // Back face
            half_side, -half_side, -half_side, // Bottom right
            -half_side, -half_side, -half_side, // Bottom left
            -half_side, half_side, -half_side, // Top left
            half_side, half_side, -half_side, // Top right
            // Top face
            -half_side, half_side, half_side, // Top left front
            half_side, half_side, half_side, // Top right front
            half_side, half_side, -half_side, // Top right back
            -half_side, half_side, -half_side, // Top left back
            // Bottom face
            -half_side, -half_side, -half_side, // Bottom left back
            half_side, -half_side, -half_side, // Bottom right back
            half_side, -half_side, half_side, // Bottom right front
            -half_side, -half_side, half_side, // Bottom left front
            // Right face
            half_side, -half_side, half_side, // Bottom right front
            half_side, -half_side, -half_side, // Bottom right back
            half_side, half_side, -half_side, // Top right back
            half_side, half_side, half_side, // Top right front
            // Left face
            -half_side, -half_side, -half_side, // Bottom left back
            -half_side, -half_side, half_side, // Bottom left front
            -half_side, half_side, half_side, // Top left front
            -half_side, half_side, -half_side // Top left back
        ];
        const colors = [];
        for (let i = 0; i < 24; i++) { // 24 vertices
            colors.push(...IColor4.to_array(color));
        }
        const indices = [
            // Front face
            0, 1, 2, 0, 2, 3,
            // Back face
            4, 5, 6, 4, 6, 7,
            // Top face
            8, 9, 10, 8, 10, 11,
            // Bottom face
            12, 13, 14, 12, 14, 15,
            // Right face
            16, 17, 18, 16, 18, 19,
            // Left face
            20, 21, 22, 20, 22, 23
        ];
        return {
            positions: new Float32Array(positions),
            indices: new Uint16Array(indices),
            colors: new Float32Array(colors)
        };
    }
    static create_circle(outer_radius, inner_radius, segments, color) {
        const positions = [];
        const colors = [];
        const indices = [];
        const angle_step = (Math.PI * 2) / segments;
        // Generate vertices for the outer circle
        for (let i = 0; i < segments; i++) {
            const angle = i * angle_step;
            const x = Math.cos(angle) * outer_radius;
            const z = Math.sin(angle) * outer_radius;
            positions.push(x, 0, z);
            colors.push(...IColor4.to_array(color));
        }
        // Generate vertices for the inner circle
        for (let i = 0; i < segments; i++) {
            const angle = i * angle_step;
            const x = Math.cos(angle) * inner_radius;
            const z = Math.sin(angle) * inner_radius;
            positions.push(x, 0, z);
            colors.push(...IColor4.to_array(color));
        }
        // Generate indices
        for (let i = 0; i < segments; i++) {
            const next_index = (i + 1) % segments;
            // Outer circle triangle
            indices.push(i, next_index, segments + i);
            indices.push(next_index, segments + next_index, segments + i);
            // Inner circle triangle (opposite side)
            indices.push(segments + i, next_index, i);
            indices.push(segments + i, segments + next_index, next_index);
        }
        return {
            positions: new Float32Array(positions.flat()),
            indices: new Uint16Array(indices),
            colors: new Float32Array(colors)
        };
    }
}
//# sourceMappingURL=MeshDescriptions.js.map